/**
 * Sidebar Component
 * Displays comment list and controls in a sidebar.
 */
import React from 'react';
import type { Comment } from '../../types';
import { CommentList } from '../CommentOverlay';
import ProgressBar from '../ProgressBar';
import { ShortcutsLegend } from '../KeyboardShortcutHint';

interface SidebarProps {
  comments: Comment[];
  selectedCommentId?: string;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: (id: string) => void;
  onDismissCategory?: (category: string) => void;
  onSelectComment: (id: string) => void;
  onGenerateFeedback?: () => void;
  isGenerating?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  comments,
  selectedCommentId,
  onAccept,
  onReject,
  onEdit,
  onDismissCategory,
  onSelectComment,
  onGenerateFeedback,
  isGenerating = false,
}) => {
  const pendingCount = comments.filter((c) => c.status === 'pending').length;
  const totalCount = comments.length;

  return (
    <aside className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-900">Comments</h2>
          <span className="text-sm text-gray-500">
            {pendingCount}/{totalCount} pending
          </span>
        </div>

        {/* Generate button */}
        {onGenerateFeedback && (
          <button
            onClick={onGenerateFeedback}
            disabled={isGenerating}
            className={`
              w-full px-4 py-2 text-sm font-medium text-white rounded-md
              ${
                isGenerating
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'
              }
              transition-all flex items-center justify-center gap-2
            `}
          >
            {isGenerating && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isGenerating ? 'Generating...' : 'Generate Feedback'}
          </button>
        )}

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="mt-3">
            <ProgressBar comments={comments} />
          </div>
        )}
      </div>

      {/* Comment list */}
      <div className="flex-1 overflow-y-auto">
        <CommentList
          comments={comments}
          selectedCommentId={selectedCommentId}
          onAccept={onAccept}
          onReject={onReject}
          onEdit={onEdit}
          onDismissCategory={onDismissCategory}
          onSelectComment={onSelectComment}
        />
      </div>

      {/* Footer stats */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div>
            <div className="font-medium text-green-600">
              {comments.filter((c) => c.status === 'accepted').length}
            </div>
            <div className="text-gray-500">Accepted</div>
          </div>
          <div>
            <div className="font-medium text-blue-600">
              {comments.filter((c) => c.status === 'edited').length}
            </div>
            <div className="text-gray-500">Edited</div>
          </div>
          <div>
            <div className="font-medium text-red-600">
              {comments.filter((c) => c.status === 'rejected').length}
            </div>
            <div className="text-gray-500">Rejected</div>
          </div>
        </div>

        {/* Keyboard shortcuts legend */}
        <ShortcutsLegend />
      </div>
    </aside>
  );
};

export default Sidebar;
